import { FileTextOutlined, PlusOutlined, PrinterOutlined } from "@ant-design/icons";
import { Button, Card, Col, DatePicker, Divider, Flex, Form, Row, Table, Typography } from "antd";
import { useForm, useWatch } from "antd/es/form/Form";
import dayjs from "dayjs";
import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { useReactToPrint } from "react-to-print";
import { perProps } from "../../../common/types/commonTypes";
import {
  Fixed2,
  formatAmount,
  formatNumber,
  rangePresets,
} from "../../../common/utils/common.utils";
import { FormInputItem } from "../../../components/common/FormItem/FormItems";
import { SelectVendors } from "../../../components/common/FormItem/SelectCustomFeilds";
import CommonViewReport from "../../../components/common/Invoice/ViewInvoiceInfo/CommonViewReport";
import BreadCrumb from "../../../components/common/breadCrumb/BreadCrumb";
import LoadingIndicator from "../../../components/common/spinner/LoadingIndicator";
import { landscapePageStyle } from "../../Reports/ledgers/pages/ClientLedgers";
import {
  useGetAllVendorPaymentQuery,
  useLazyDownloadVendorPayExcelQuery,
} from "../api/endpoints/vendorPaymentEndpoints";
import { VendorPaymentColumns } from "../utils/VendorPaymentColumns";

const ListOfVendPayment = ({ permission }: perProps) => {
  const [form] = useForm();
  const componentRef = useRef(null);
  // Get Data with Pagination and Search
  const [search, setSearch] = useState("");
  const [date, setDate] = useState([]);

  const date_range = useWatch("date_range", form);
  const com_vendor = useWatch("com_vendor", form);

  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 20,
  });

  const handlePaginationChange = (current: number, pageSize: number) => {
    setPagination((prevPagination) => ({
      ...prevPagination,
      current,
      pageSize,
    }));
  };

  const {
    data: allVendors,
    isLoading,
    refetch,
  } = useGetAllVendorPaymentQuery({
    ...pagination,
    search,
    from_date: Array.isArray(date_range) ? dayjs(date_range[0]).format("YYYY-MM-DD") : undefined,
    to_date: Array.isArray(date_range) ? dayjs(date_range[1]).format("YYYY-MM-DD") : undefined,
    com_vendor: com_vendor,
  });

  console.log(allVendors?.data?.dateSummary);

  const [downloadExcel] = useLazyDownloadVendorPayExcelQuery();

  const totalAmount = allVendors?.data?.data?.reduce((prev: any, curr: any) => {
    return prev + curr.payment_amount;
  }, 0);

  const handleSearch = (e: any) => {
    const search = e.target.value;

    setSearch(search);
  };

  const handleDateRangeChange = (date: any, dateString: any) => {
    setPagination((prev) => ({
      ...prev,
      current: 1,
      pageSize: 20,
    }));
    setDate(dateString);
  };
  const vendorData = allVendors?.data?.summary
    ? Object.entries(allVendors.data.summary).map(([id, vendor]: any) => ({
        key: id,
        vendor_name: vendor.vendor_name,
        total_payment: vendor.total_payment,
      }))
    : [];

  const columns = [
    {
      title: "Vendor Name",
      dataIndex: "vendor_name",
      key: "vendor_name",
    },
    {
      title: "Total Payment",
      dataIndex: "total_payment",
      key: "total_payment",
      render: (text: number) => (
        <span className="font-medium text-blue-600">{text.toLocaleString()}</span>
      ),
    },
  ];
  const jsx = (col: any) => {
    return (
      <>
        <Table
          bordered
          size="small"
          rowKey={(e) => e.vpay_id}
          columns={col}
          dataSource={allVendors?.data?.data}
          scroll={{ x: true }}
          loading={{
            spinning: isLoading,
            indicator: <LoadingIndicator />,
          }}
          pagination={
            allVendors?.count !== undefined && allVendors?.count < 20
              ? false
              : {
                  ...pagination,
                  total: allVendors?.count,
                  showSizeChanger: true,
                  pageSizeOptions: ["20", "50", "100", "200", "500"],
                  onChange: handlePaginationChange,
                }
          }
          summary={() => {
            return (
              <>
                <Table.Summary.Row>
                  <Table.Summary.Cell index={0} colSpan={7} className="bold">
                    TOTAL
                  </Table.Summary.Cell>

                  <Table.Summary.Cell index={2}>
                    <div style={{ textAlign: "right" }}>
                      <Typography.Text className="color-profit bold">
                        {formatNumber(totalAmount)}
                      </Typography.Text>
                    </div>
                  </Table.Summary.Cell>
                  <Table.Summary.Cell index={0} colSpan={2} />
                </Table.Summary.Row>
              </>
            );
          }}
        />

        <Divider orientation="left" plain>
          <Typography.Title style={{ fontFamily: "sans-serif" }} level={5}>
            Vendor Payment Summary
          </Typography.Title>
        </Divider>
        <div style={{ maxWidth: 850, margin: "0px auto" }}>
          <Table bordered columns={columns} dataSource={vendorData} pagination={false} />
        </div>

        <Divider orientation="left" plain>
          <Typography.Title style={{ fontFamily: "sans-serif" }} level={5}>
            Date wise summary
          </Typography.Title>
        </Divider>
        <div style={{ maxWidth: 850, margin: "0px auto" }}>
          <Table
            dataSource={allVendors?.data?.dateSummary}
            columns={[
              {
                title: "SL.",
                render: (_, data, index) => <>{index + 1}</>,
              },
              {
                title: "Date",
                render: (_, data) => <>{dayjs(data.payment_date).format("DD-MMM-YYYY")}</>,
              },
              {
                title: "Payment Amount",
                align: "right",
                render: (_, data) => <>{formatAmount(Fixed2(data.total_payment))}</>,
              },
            ]}
            size="small"
            bordered
            pagination={false}
            summary={(_) => {
              const total = allVendors?.data?.dateSummary.reduce(
                (acc: any, item: any) => acc + Number(item.total_payment),
                0,
              );
              return (
                <Table.Summary.Row>
                  <Table.Summary.Cell index={0} colSpan={2}>
                    <div style={{ textAlign: "right", marginRight: 10 }}>
                      <Typography.Text strong>
                        Total of Date wise Payment Amount{"  "}
                      </Typography.Text>
                    </div>
                  </Table.Summary.Cell>
                  <Table.Summary.Cell index={0} colSpan={1}>
                    <div style={{ textAlign: "right", marginRight: 5 }}>
                      <Typography.Text strong className="nowrap">
                        {formatAmount(Fixed2(total))}
                      </Typography.Text>
                    </div>
                  </Table.Summary.Cell>
                </Table.Summary.Row>
              );
            }}
          />
        </div>
      </>
    );
  };

  const print_content = (
    <div hidden>
      <CommonViewReport
        children={jsx(
          VendorPaymentColumns({
            pagination,
            isForPrint: true,
            permission,
          }),
        )}
        printRef={componentRef}
        title={{ info_title: "Vendor Payment List", title: "" }}
      />
    </div>
  );

  const handlePrint = useReactToPrint({
    content: () => componentRef.current,
    documentTitle: `Vendor_payment_list_$${dayjs()}`,
    pageStyle: landscapePageStyle,
  });

  const handleExcel = () => {
    downloadExcel({ from_date: date[0], to_date: date[1], com_vendor });
  };

  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const yearFormDate = searchParams.get("daily");

    const monthFormDate = searchParams.get("from_date");
    const monthToDate = searchParams.get("to_date");

    if (monthFormDate && monthToDate) {
      form.setFieldValue("date_range", [dayjs(monthFormDate), dayjs(monthToDate)]);
    }

    if (yearFormDate) {
      form.setFieldsValue({
        date_range: [dayjs(yearFormDate), dayjs(yearFormDate)],
      });
    }
  }, [location.search]);

  return (
    <>
      <BreadCrumb
        arrOfOption={["List Of Vendor Payments"]}
        refetch={refetch}
        reloaderSize="small"
      />

      <Row justify={"space-between"}>
        <Col lg={4}>
          <Flex gap={5}>
            {permission?.["create:any"] && (
              <Link to="/vendors/payment/add" state={location.pathname}>
                <Button type="primary" icon={<PlusOutlined />}>
                  Add Payment
                </Button>
              </Link>
            )}
            <Button icon={<PrinterOutlined />} type="primary" onClick={handlePrint}>
              Print
            </Button>

            <Button icon={<FileTextOutlined />} type="primary" onClick={handleExcel}>
              Excel
            </Button>
          </Flex>
        </Col>

        <Col span={12}>
          <Form
            form={form}
            initialValues={{
              date_range: [dayjs().subtract(15, "days"), dayjs()],
            }}
          >
            <Row justify={"end"} gutter={10}>
              <Col xs={24} sm={12} md={8} lg={8}>
                <Form.Item name="date_range">
                  <DatePicker.RangePicker
                    presets={rangePresets}
                    format={"YYYY-MM-DD"}
                    onChange={handleDateRangeChange}
                    allowClear={true}
                  />
                </Form.Item>
              </Col>
              <SelectVendors name="com_vendor" size={8} mdSize={8} smSize={24} offDropDown />
              <Col xs={24} sm={12} md={8} lg={8}>
                <FormInputItem
                  onBlur={handleSearch}
                  onPressEnter={handleSearch}
                  placeholder="🔍 Search by combine"
                  size={24}
                  allowClear={true}
                />
              </Col>
            </Row>
          </Form>
        </Col>
      </Row>

      {jsx(
        VendorPaymentColumns({
          pagination,
          isForPrint: false,
          permission,
        }),
      )}

      {print_content}
    </>
  );
};

export default ListOfVendPayment;
